import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, Plus, ExternalLink, Trash2, Save, DollarSign, Send,
  RefreshCw, Loader2, X, ChevronRight, Archive, Copy, Mail, MailCheck, Bell, BellOff, Link as LinkIcon,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { api } from './api';
import { logger } from '../lib/logger';

/**
 * AdminClients — coach's word-of-mouth client roster.
 *
 * Two-pane layout: list on the left, detail drawer on the right when a row
 * is selected. Detail has three tabs — Profile, Sessions, Payments.
 * Each row can also generate a magic-link invite to the app that pre-applies
 * the client's comp access grants on acceptance.
 */

const EMPTY_CLIENT = {
  name: '', email: '', phone: '',
  birth_year: '', sport: '', position: '', level: '', school_team: '',
  parent_name: '', parent_email: '', parent_phone: '',
  notes: '',
  access_grants: { ai_coach: false, locker_room: false, one_on_one: false },
  app_login_enabled: false,
  tags: [],
};

const INVITE_STATUS_META = {
  none:     { label: 'Not invited',   tone: 'muted' },
  sent:     { label: 'Invite sent',   tone: 'primary' },
  link:     { label: 'Link ready',    tone: 'accent' },
  accepted: { label: 'Signed up',     tone: 'success' },
  expired:  { label: 'Invite expired', tone: 'warn' },
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState('profile');
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/coach-clients');
      setClients(data?.clients || []);
    } catch (err) {
      logger.error('AdminClients load failed', err);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const data = await api.get(`/api/admin/coach-clients/${id}`);
      setDetail(data);
    } catch (err) {
      logger.error('AdminClients detail failed', err);
      toast.error('Failed to load client details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      // Strip empty-string fields — the backend uses Optional[EmailStr] and
      // Optional[int] which reject "" outright. Keep booleans and arrays.
      const cleaned = {};
      Object.entries(payload).forEach(([k, v]) => {
        if (typeof v === 'string' && v.trim() === '') return;
        cleaned[k] = v;
      });
      cleaned.birth_year = payload.birth_year ? Number(payload.birth_year) : null;
      const doc = await api.post('/api/admin/coach-clients', cleaned);
      toast.success('Client added');
      setShowCreate(false);
      await loadClients();
      setSelectedId(doc.id);
    } catch (err) {
      logger.error(err);
      toast.error('Create failed', { description: String(err?.message || err) });
    } finally { setSaving(false); }
  };

  const handleUpdate = async (patch) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const cleaned = {};
      Object.entries(patch).forEach(([k, v]) => {
        // Preserve nested objects (access_grants) and arrays (tags) untouched.
        if (typeof v === 'string' && v.trim() === '') { cleaned[k] = null; return; }
        cleaned[k] = v;
      });
      if (cleaned.birth_year !== undefined) {
        cleaned.birth_year = cleaned.birth_year ? Number(cleaned.birth_year) : null;
      }
      const updated = await api.put(`/api/admin/coach-clients/${selectedId}`, cleaned);
      setDetail(updated);
      await loadClients();
      toast.success('Saved');
    } catch (err) {
      logger.error(err);
      toast.error('Save failed', { description: String(err?.message || err) });
    } finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!selectedId) return;
    if (!window.confirm('Archive this client? They will no longer appear in the active list.')) return;
    try {
      await api.delete(`/api/admin/coach-clients/${selectedId}`);
      toast.success('Client archived');
      setSelectedId(null);
      await loadClients();
    } catch { toast.error('Archive failed'); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-clients-page">
      <header className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" /> Client Roster
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Word-of-mouth clients. Access grants, session notes, one-off Stripe billing, and magic-link invites.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadClients} disabled={loading} data-testid="clients-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="clients-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add client
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* LIST */}
        <div className="border rounded-2xl bg-card">
          <div className="p-3 border-b text-xs uppercase tracking-widest font-mono-accent text-muted-foreground">
            {clients.length} active
          </div>
          <div className="max-h-[75vh] overflow-y-auto divide-y">
            {clients.length === 0 && !loading && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                No clients yet. Click &quot;Add client&quot; to create your first.
              </div>
            )}
            {clients.map((c) => {
              const isSel = c.id === selectedId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left p-3 hover:bg-accent/40 transition-colors ${isSel ? 'bg-accent' : ''}`}
                  data-testid={`clients-row-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold truncate">{c.name || '(unnamed)'}</div>
                        <InviteStatusBadge status={c.invite_status} compact />
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{c.email || c.phone || '—'}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {c.sport ? `${c.sport}${c.position ? ` · ${c.position}` : ''}` : ''}
                        {c.session_count > 0 && (
                          <span className="ml-2">{c.session_count} sessions</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* DETAIL */}
        <div className="border rounded-2xl bg-card min-h-[70vh]">
          {!selectedId && (
            <div className="p-10 text-center text-muted-foreground">
              Select a client to view profile, sessions, and billing.
            </div>
          )}
          {selectedId && detailLoading && (
            <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          )}
          {selectedId && detail && (
            <ClientDetail
              client={detail}
              tab={tab}
              setTab={setTab}
              onUpdate={handleUpdate}
              onArchive={handleArchive}
              saving={saving}
              onReload={() => loadDetail(selectedId)}
              onListReload={loadClients}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          saving={saving}
        />
      )}
    </div>
  );
}

// -------- Invite status badge --------
function InviteStatusBadge({ status, compact }) {
  const meta = INVITE_STATUS_META[status] || INVITE_STATUS_META.none;
  if (status === 'none' && compact) return null;
  const toneClass = {
    muted:   'bg-muted text-muted-foreground',
    primary: 'bg-primary/15 text-primary',
    accent:  'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    success: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    warn:    'bg-red-500/15 text-red-700 dark:text-red-300',
  }[meta.tone] || 'bg-muted';
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-mono-accent uppercase tracking-wider ${toneClass}`}
      data-testid={`invite-badge-${status}`}
    >
      {meta.label}
    </span>
  );
}

// -------- Detail view --------
function ClientDetail({ client, tab, setTab, onUpdate, onArchive, saving, onReload, onListReload }) {
  const [profile, setProfile] = useState(client);
  useEffect(() => { setProfile(client); }, [client]);

  const dirty = useMemo(() => JSON.stringify(profile) !== JSON.stringify(client), [profile, client]);

  const saveProfile = () => {
    // Do not send server-managed fields back on update.
    const {
      id, created_at, updated_at, sessions, payments, linked_user_id,
      invite, status, invite_status, session_count, payment_count, last_session_at,
      ...patch
    } = profile;
    void id; void created_at; void updated_at; void sessions; void payments;
    void linked_user_id; void invite; void status; void invite_status;
    void session_count; void payment_count; void last_session_at;
    onUpdate(patch);
  };

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setGrant = (k, v) =>
    setProfile((p) => ({ ...p, access_grants: { ...(p.access_grants || {}), [k]: v } }));

  return (
    <div>
      <div className="border-b p-4 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-xl font-bold">{client.name}</h2>
            <InviteStatusBadge status={inviteStatusOf(client)} />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {client.email || '—'}{client.phone ? ` · ${client.phone}` : ''}
          </div>
        </div>
        <div className="flex gap-2">
          {['profile', 'sessions', 'invite', 'payments'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider ${tab === t ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
              data-testid={`clients-tab-${t}`}
            >{t}</button>
          ))}
        </div>
      </div>

      {tab === 'profile' && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" value={profile.name} onChange={(v) => setField('name', v)} />
            <Field label="Email" value={profile.email || ''} onChange={(v) => setField('email', v)} />
            <Field label="Phone" value={profile.phone || ''} onChange={(v) => setField('phone', v)} />
            <Field label="Birth Year" value={profile.birth_year || ''} onChange={(v) => setField('birth_year', v)} />
            <Field label="Sport" value={profile.sport || ''} onChange={(v) => setField('sport', v)} />
            <Field label="Position" value={profile.position || ''} onChange={(v) => setField('position', v)} />
            <Field label="Level (e.g. HS Varsity)" value={profile.level || ''} onChange={(v) => setField('level', v)} />
            <Field label="School / Team" value={profile.school_team || ''} onChange={(v) => setField('school_team', v)} />
            <Field label="Parent Name" value={profile.parent_name || ''} onChange={(v) => setField('parent_name', v)} />
            <Field label="Parent Email" value={profile.parent_email || ''} onChange={(v) => setField('parent_email', v)} />
            <Field label="Parent Phone" value={profile.parent_phone || ''} onChange={(v) => setField('parent_phone', v)} />
          </div>
          <div>
            <Label>Notes (running)</Label>
            <Textarea rows={5} value={profile.notes || ''} onChange={(e) => setField('notes', e.target.value)} data-testid="client-notes-field" />
          </div>
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
            <div className="text-xs font-mono-accent uppercase tracking-widest text-muted-foreground">
              Access Grants (comp) — applied on invite acceptance
            </div>
            <div className="text-[11px] text-muted-foreground -mt-1 mb-1">
              1:1 or Locker Room → Premium 6mo. AI Coach only → Pro 3mo. None → Free account.
            </div>
            <GrantSwitch label="Apex AI Coach Pro" value={profile.access_grants?.ai_coach} onChange={(v) => setGrant('ai_coach', v)} />
            <GrantSwitch label="Locker Room Pass" value={profile.access_grants?.locker_room} onChange={(v) => setGrant('locker_room', v)} />
            <GrantSwitch label="1:1 Coaching (Premium)" value={profile.access_grants?.one_on_one} onChange={(v) => setGrant('one_on_one', v)} />
            <GrantSwitch label="App login enabled" value={profile.app_login_enabled} onChange={(v) => setField('app_login_enabled', v)} />
          </div>
          <div className="flex gap-2 justify-between">
            <Button variant="outline" size="sm" onClick={onArchive} className="text-red-600 hover:text-red-700" data-testid="client-archive-btn">
              <Archive className="h-4 w-4 mr-1.5" /> Archive
            </Button>
            <Button size="sm" onClick={saveProfile} disabled={!dirty || saving} data-testid="client-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save changes
            </Button>
          </div>
        </div>
      )}

      {tab === 'sessions' && <SessionsTab client={client} onReload={onReload} />}
      {tab === 'invite'   && <InviteTab   client={client} onReload={onReload} onListReload={onListReload} />}
      {tab === 'payments' && <PaymentsTab client={client} onReload={onReload} />}
    </div>
  );
}

function inviteStatusOf(client) {
  if (client.linked_user_id) return 'accepted';
  const inv = client.invite;
  if (!inv) return 'none';
  if (inv.accepted_at) return 'accepted';
  if (inv.expires_at && new Date(inv.expires_at) < new Date()) return 'expired';
  return inv.sent_via_email ? 'sent' : 'link';
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function GrantSwitch({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <Switch checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

// -------- Sessions tab --------
function SessionsTab({ client, onReload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDrafted, setAiDrafted] = useState(false);
  const emptyForm = {
    duration_minutes: 60, format: 'video', notes: '',
    next_steps_athlete: '', next_steps_coach: '', follow_up_at: '',
  };
  const [form, setForm] = useState(emptyForm);

  const generateAi = async () => {
    if (!form.notes || form.notes.trim().length < 10) {
      toast.error('Add session notes first', {
        description: 'The AI needs at least a couple sentences of context.',
      });
      return;
    }
    setAiDrafting(true);
    try {
      const res = await api.post(
        `/api/admin/coach-clients/${client.id}/sessions/ai-draft`,
        { notes: form.notes },
      );
      setForm((f) => ({
        ...f,
        next_steps_athlete: res.next_steps_athlete || f.next_steps_athlete,
        next_steps_coach: res.next_steps_coach || f.next_steps_coach,
      }));
      setAiDrafted(true);
      toast.success('Draft ready — edit before saving');
    } catch (err) {
      logger.error(err);
      toast.error('AI draft failed', { description: String(err?.message || err) });
    } finally { setAiDrafting(false); }
  };

  const addSession = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.follow_up_at) {
        // datetime-local yields "YYYY-MM-DDTHH:MM" — append seconds+Z so backend parses cleanly.
        payload.follow_up_at = new Date(payload.follow_up_at).toISOString();
      } else {
        payload.follow_up_at = null;
      }
      await api.post(`/api/admin/coach-clients/${client.id}/sessions`, payload);
      toast.success('Session logged');
      setShowAdd(false);
      setForm(emptyForm);
      setAiDrafted(false);
      onReload();
    } catch (err) {
      toast.error('Save failed', { description: String(err?.message || err) });
    }
    finally { setSaving(false); }
  };

  const removeSession = async (sid) => {
    if (!window.confirm('Delete this session record?')) return;
    try {
      await api.delete(`/api/admin/coach-clients/${client.id}/sessions/${sid}`);
      onReload();
    } catch { toast.error('Delete failed'); }
  };

  const [recapModalSession, setRecapModalSession] = useState(null); // session obj being previewed
  const openRecap = (session) => {
    if (!client.email) {
      toast.error('No email on file for this client', {
        description: 'Add one on the Profile tab to send recaps.',
      });
      return;
    }
    if (!session.next_steps_athlete || session.next_steps_athlete.trim().length === 0) {
      toast.error('No athlete next steps on this session', {
        description: 'Add them (or regenerate with AI) before sending a recap.',
      });
      return;
    }
    setRecapModalSession(session);
  };

  const sessions = (client.sessions || []).slice().reverse();

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="session-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Log session
        </Button>
      </div>
      {showAdd && (
        <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Format</Label>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="w-full border rounded-md px-3 py-2 bg-background">
                <option value="video">Video</option>
                <option value="phone">Phone</option>
                <option value="in-person">In-person</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Session notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => { setForm({ ...form, notes: e.target.value }); setAiDrafted(false); }} data-testid="session-notes-input" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-[11px] text-muted-foreground">
                Type what happened, then let AI draft next steps below.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateAi}
                disabled={aiDrafting || !form.notes || form.notes.trim().length < 10}
                data-testid="session-ai-draft-btn"
              >
                {aiDrafting
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Drafting…</>
                  : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {aiDrafted ? 'Regenerate with AI' : 'Generate next steps with AI'}</>
                }
              </Button>
            </div>
            {aiDrafted && (
              <p className="text-[11px] text-primary mt-1 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> AI-drafted — review and edit before saving.
              </p>
            )}
          </div>
          <div>
            <Label>Next steps — for the athlete</Label>
            <Textarea rows={4} value={form.next_steps_athlete} onChange={(e) => setForm({ ...form, next_steps_athlete: e.target.value })} data-testid="session-athlete-steps" />
          </div>
          <div>
            <Label>Next steps — for me</Label>
            <Textarea rows={4} value={form.next_steps_coach} onChange={(e) => setForm({ ...form, next_steps_coach: e.target.value })} data-testid="session-coach-steps" />
          </div>
          <div>
            <Label className="flex items-center gap-1">
              <Bell className="h-3.5 w-3.5" /> Follow-up date/time — 24h reminder to the athlete (optional)
            </Label>
            <Input
              type="datetime-local"
              value={form.follow_up_at}
              onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })}
              data-testid="session-followup-input"
            />
            {!client.email && form.follow_up_at && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                No email on file — reminder will be logged but not sent. Add an email to the profile to enable delivery.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button size="sm" onClick={addSession} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save session
            </Button>
          </div>
        </div>
      )}
      {sessions.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">No sessions logged yet.</div>}
      {sessions.map((s) => (
        <div key={s.id} className="border rounded-lg p-3" data-testid={`session-${s.id}`}>
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="text-sm font-semibold">{new Date(s.date).toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">{s.duration_minutes} min · {s.format}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeSession(s.id)} className="h-7 w-7 text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          {s.notes && <div className="text-sm mb-2 whitespace-pre-wrap"><strong>Notes:</strong> {s.notes}</div>}
          {s.next_steps_athlete && <div className="text-sm mb-1 whitespace-pre-wrap"><strong>Athlete →</strong> {s.next_steps_athlete}</div>}
          {s.next_steps_coach && <div className="text-sm mb-2 whitespace-pre-wrap"><strong>Me →</strong> {s.next_steps_coach}</div>}

          {s.next_steps_athlete && (
            <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between gap-2 flex-wrap">
              {s.recap_sent_at ? (
                <div className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                  <MailCheck className="h-3.5 w-3.5" />
                  Recap sent {new Date(s.recap_sent_at).toLocaleString()}
                  {s.recap_sent_to && <span className="text-muted-foreground"> · {s.recap_sent_to}</span>}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Send this recap to the athlete as a clean take-home note.
                </div>
              )}
              <Button
                size="sm"
                variant={s.recap_sent_at ? 'outline' : 'default'}
                onClick={() => openRecap(s)}
                disabled={!client.email}
                data-testid={`session-recap-btn-${s.id}`}
                title={!client.email ? 'Add an email to this client to enable recap emails' : ''}
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {s.recap_sent_at ? 'Resend recap' : (client.email ? `Preview & send to ${(client.email.length > 22 ? client.email.slice(0,20) + '…' : client.email)}` : 'Email recap (no email)')}
              </Button>
            </div>
          )}

          {s.follow_up_at && (
            <div className="text-xs mt-2 pt-2 border-t border-border/60 flex items-center gap-1.5">
              {s.reminder_sent_at
                ? <><BellOff className="h-3 w-3 text-emerald-600" /><span className="text-emerald-700 dark:text-emerald-400">Reminder sent for {new Date(s.follow_up_at).toLocaleString()}</span></>
                : <><Bell className="h-3 w-3 text-primary" /><span>Reminder scheduled for 24h before {new Date(s.follow_up_at).toLocaleString()}</span></>
              }
            </div>
          )}
        </div>
      ))}

      {recapModalSession && (
        <RecapPreviewModal
          client={client}
          session={recapModalSession}
          initialNote={recapModalSession.recap_last_note || ''}
          onClose={() => setRecapModalSession(null)}
          onSent={() => { setRecapModalSession(null); onReload(); }}
        />
      )}
    </div>
  );
}

// -------- Recap Preview Modal --------
function RecapPreviewModal({ client, session, initialNote, onClose, onSent }) {
  const [note, setNote] = useState(initialNote);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewErr, setPreviewErr] = useState('');
  const [sending, setSending] = useState(false);

  // Debounced preview fetch — 250ms after last note keystroke.
  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewErr('');
    const t = setTimeout(async () => {
      try {
        const res = await api.post(
          `/api/admin/coach-clients/${client.id}/sessions/${session.id}/recap-preview`,
          { personal_note: note || null },
        );
        if (cancelled) return;
        setPreview(res);
      } catch (err) {
        if (cancelled) return;
        setPreviewErr(String(err?.message || err));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [client.id, session.id, note]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(
        `/api/admin/coach-clients/${client.id}/sessions/${session.id}/recap-email`,
        { personal_note: note || null },
      );
      toast.success('Recap sent', { description: `Emailed to ${client.email}` });
      onSent();
    } catch (err) {
      logger.error(err);
      toast.error('Recap failed', { description: String(err?.message || err) });
    } finally { setSending(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recap-preview-modal"
    >
      <div
        className="bg-background rounded-2xl border max-w-3xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-heading text-lg font-bold">Preview recap</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Sending to <span className="font-mono">{client.email}</span>
              {preview?.subject && <> &middot; Subject: <span className="italic">{preview.subject}</span></>}
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-accent" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b">
          <Label htmlFor="recap-personal-note" className="flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" /> Add a personal note (optional) — appears above the next steps
          </Label>
          <Textarea
            id="recap-personal-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={`Great session today, ${(client.name || '').split(' ')[0] || 'athlete'}. Proud of the work you put in — keep the momentum going.`}
            maxLength={2000}
            data-testid="recap-personal-note"
          />
          <p className="text-[11px] text-muted-foreground text-right mt-1">
            {note.length}/2000
          </p>
        </div>

        <div className="flex-1 overflow-hidden bg-muted/30 p-3">
          <div className="text-[11px] font-mono-accent uppercase tracking-widest text-muted-foreground mb-2 px-1">
            Live email preview
          </div>
          {previewLoading && !preview && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {previewErr && (
            <div className="text-sm text-red-600 dark:text-red-400 p-4">
              Preview failed: {previewErr}
            </div>
          )}
          {preview && (
            <div className="relative w-full h-[52vh] rounded-lg overflow-hidden border">
              <iframe
                title="recap-email-preview"
                srcDoc={preview.html}
                sandbox=""
                className="w-full h-full bg-[#0a0d0e]"
                data-testid="recap-preview-iframe"
              />
              {previewLoading && (
                <div className="absolute top-2 right-2 bg-background/90 rounded-md px-2 py-1 text-xs flex items-center gap-1.5 shadow">
                  <Loader2 className="h-3 w-3 animate-spin" /> Updating…
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {session.recap_sent_at
              ? `Previously sent ${new Date(session.recap_sent_at).toLocaleString()}`
              : 'This will send immediately when you hit Send.'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button size="sm" onClick={send} disabled={sending || previewLoading || !preview} data-testid="recap-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {session.recap_sent_at ? 'Resend now' : 'Send now'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------- Invite tab --------
function InviteTab({ client, onReload, onListReload }) {
  const [busy, setBusy] = useState(false);
  const invite = client.invite;
  const status = inviteStatusOf(client);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/api/admin/coach-clients/${client.id}/invite`, {});
      try { await navigator.clipboard.writeText(res.invite_url); } catch { /* no-op */ }
      const desc = res.sent_via_email
        ? `Emailed to ${res.to_email}. Link also copied to clipboard.`
        : 'Link copied to clipboard — share it with the client directly.';
      toast.success('Invite ready', { description: desc });
      onReload(); onListReload && onListReload();
    } catch (err) {
      toast.error('Invite failed', { description: String(err?.message || err) });
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this invite link? The current URL will stop working.')) return;
    setBusy(true);
    try {
      await api.delete(`/api/admin/coach-clients/${client.id}/invite`);
      toast.success('Invite revoked');
      onReload(); onListReload && onListReload();
    } catch { toast.error('Revoke failed'); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!invite?.token) return;
    const origin = window.location.origin;
    const url = `${origin}/app/invite/${invite.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Copy failed', { description: url });
    }
  };

  const tierPreview = invite?.tier_preview || client.invite?.tier_preview;
  const grants = client.access_grants || {};
  const impliedTier = tierPreview
    || (grants.one_on_one || grants.locker_room
        ? { tier: 'premium', months: 6, label: 'Premium · 6 months' }
        : grants.ai_coach
          ? { tier: 'pro', months: 3, label: 'Pro · 3 months' }
          : { tier: 'free', months: 0, label: 'Free account only' });

  return (
    <div className="p-4 space-y-4">
      <div className="border rounded-lg p-4 bg-muted/20 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-mono-accent uppercase tracking-widest text-muted-foreground">Magic-link invite</div>
            <div className="font-heading text-lg font-bold mt-0.5">{INVITE_STATUS_META[status].label}</div>
          </div>
          <InviteStatusBadge status={status} />
        </div>

        <div className="text-sm text-muted-foreground">
          On acceptance, {client.name || 'this client'} will be granted{' '}
          <strong className="text-foreground">{impliedTier.label}</strong> automatically.{' '}
          {!client.email && (
            <span className="text-amber-600 dark:text-amber-400">
              No email on file — you’ll get a shareable link to text or DM.
            </span>
          )}
        </div>

        {invite?.token && !invite.accepted_at && (
          <div className="border rounded-md p-3 bg-background flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
            <code className="text-[11px] font-mono truncate flex-1" data-testid="invite-url-preview">
              /app/invite/{invite.token.slice(0, 12)}…
            </code>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="invite-copy-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          {status === 'accepted' ? (
            <div className="text-sm text-emerald-700 dark:text-emerald-400">
              Client signed up and access has been applied.
            </div>
          ) : (
            <>
              {invite?.token && (
                <Button variant="outline" size="sm" onClick={revoke} disabled={busy} data-testid="invite-revoke-btn">
                  Revoke
                </Button>
              )}
              <Button size="sm" onClick={generate} disabled={busy || !!client.linked_user_id} data-testid="invite-generate-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (
                  client.email ? <Mail className="h-4 w-4 mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />
                )}
                {invite?.token ? 'Regenerate & re-send' : (client.email ? 'Send email invite' : 'Generate link')}
              </Button>
            </>
          )}
        </div>

        {invite?.expires_at && !invite.accepted_at && (
          <div className="text-[11px] text-muted-foreground">
            Expires {new Date(invite.expires_at).toLocaleString()}
          </div>
        )}
      </div>

      {client.linked_user_id && (
        <div className="border rounded-lg p-3 bg-muted/30 flex items-center gap-2">
          <Badge variant="secondary">Linked</Badge>
          <span className="text-sm text-muted-foreground truncate">
            User id: <code className="text-xs">{client.linked_user_id}</code>
          </span>
        </div>
      )}
    </div>
  );
}

// -------- Payments tab --------
function PaymentsTab({ client, onReload }) {
  const [amount, setAmount] = useState('175.00');
  const [desc, setDesc] = useState('Coaching session');
  const [saving, setSaving] = useState(false);

  const createLink = async () => {
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!amountCents || amountCents < 100) {
      toast.error('Amount must be at least $1.00');
      return;
    }
    setSaving(true);
    try {
      const payment = await api.post(`/api/admin/coach-clients/${client.id}/payment-link`, {
        amount_cents: amountCents, description: desc,
      });
      try { await navigator.clipboard.writeText(payment.stripe_url); } catch { /* ok */ }
      toast.success('Payment link created — copied to clipboard', {
        description: payment.stripe_url,
      });
      onReload();
    } catch (err) {
      toast.error('Stripe link failed', { description: String(err?.message || err) });
    } finally { setSaving(false); }
  };

  const payments = (client.payments || []).slice().reverse();

  return (
    <div className="p-4 space-y-3">
      <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
        <div className="text-xs font-mono-accent uppercase tracking-widest text-muted-foreground">Create Payment Link</div>
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <div>
            <Label>Amount USD</Label>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="175.00" data-testid="payment-amount" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="payment-description" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={createLink} disabled={saving} data-testid="payment-create-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1.5" />}
            Generate Stripe link
          </Button>
        </div>
      </div>
      {payments.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No payments yet.</div>}
      {payments.map((p) => (
        <div key={p.id} className="border rounded-lg p-3 flex justify-between items-center" data-testid={`payment-${p.id}`}>
          <div>
            <div className="font-semibold">${(p.amount_cents / 100).toFixed(2)} — {p.description}</div>
            <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</div>
          </div>
          <a href={p.stripe_url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
            Open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ))}
    </div>
  );
}

// -------- Create modal --------
function CreateModal({ onClose, onSubmit, saving }) {
  const [form, setForm] = useState(EMPTY_CLIENT);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-2xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-heading text-lg font-bold">Add client</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *" value={form.name} onChange={(v) => set('name', v)} />
            <Field label="Email" value={form.email} onChange={(v) => set('email', v)} />
            <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} />
            <Field label="Birth Year" value={form.birth_year} onChange={(v) => set('birth_year', v)} />
            <Field label="Sport" value={form.sport} onChange={(v) => set('sport', v)} />
            <Field label="Position" value={form.position} onChange={(v) => set('position', v)} />
            <Field label="Level" value={form.level} onChange={(v) => set('level', v)} />
            <Field label="School / Team" value={form.school_team} onChange={(v) => set('school_team', v)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSubmit(form)} disabled={saving || !form.name}>
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Create
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
